/* eslint-disable no-prototype-builtins */
import Toaster from "../components/modal/toaster/Toaster";
import bankRoundingUtil from "@/bankRoundingUtil";
import { ORIENTATION, SENTRY_ERRORS } from "@/../app-constants";
import { LANGUAGES, DISPLAY, MODAL, TOASTER_NAME } from "../../app-constants";
import Dialog from "@/components/modal/dialog/Dialog";
import errorLoggerAdapter from "../adapters/errorLoggerAdapter";

export function capitalizeWord(query) {
  if (!query) return "";
  query = query.toString();
  return query.charAt(0).toUpperCase() + query.slice(1).toLowerCase();
}

export function capFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * This method displays the regular dialog box.
 * By default it shows a close button. This option can be disabled with the fourth parameter.
 *
 * @export
 * @param {string} title
 * @param {string} text
 * @param {*} [buttons=[]]
 * @param {boolean} [showCloseButton=true]
 * @param {function} [callbackOnClose=null] This callback will be called when the close button is triggered
 * @return {string}
 */
export function showDialog(
  vm,
  title,
  text,
  buttons = [],
  showCloseButton = true,
  showFirstActive = false,
  callbackOnClose = null
) {
  const windowWidth = window.innerWidth;
  const width = windowWidth < DISPLAY.minDesktopWidth ? windowWidth - MODAL.dialogMobileMargins : MODAL.dialogDesktopWidth;

  // Add a close button by default to maintain backward-compatibility
  if (showCloseButton) {
    const closeButton = {
      title: "Close",
      handler: () => {
        vm.$modal.hide(MODAL.dialogName);
        if (callbackOnClose) callbackOnClose();
      }
    };

    buttons = [closeButton, ...buttons];
  }

  vm.$modal?.show(
    Dialog,
    {
      showCloseButton,
      title,
      text,
      buttons,
      showFirstActive
    },
    {
      name: MODAL.dialogName,
      alwaysShowBoarderRadius: true,
      width,
      height: "auto",
      transition: "fade",
      classes: "modal--dialog"
    }
  );
}

export function showToaster(error) {
  const { title, errorMessage } = error;
  this &&
    this.$modal &&
    this.$modal.show(
      Toaster,
      {
        title,
        errorMessage
      },
      {
        name: TOASTER_NAME,
        alwaysShowBoarderRadius: true,
        height: "auto",
        width: 350,
        pivotY: 0,
        transition: "fade",
        classes: "modal-toaster"
      }
    );
}

export function isNumeric(value) {
  return !isNaN(parseFloat(value)) && isFinite(value);
}

export const scrollTargetDirective = {
  mounted(el) {
    el.scrollIntoView();
  }
};

export function roundOffByBankApproach(value) {
  return bankRoundingUtil(value);
}

/**
 * Checks container scroll size against client size and returns a boolean.
 * @param element, dom element
 * @param scrollDirection, string, vertical / horizontal
 * @returns {boolean}
 */
export function isScrollBarPresent(element, scrollDirection = "vertical") {
  if (!element) return false;

  const type = scrollDirection === "vertical" ? "Height" : "Width";
  return element[`scroll${type}`] > element[`client${type}`];
}

/**
 * tracks if an elements scroll bar is present, if element is being scrolled,
 * if element has been scrolled to bottom. Will update on screen resize.
 * @param scrollCont, can be class or id, element for checking scroll
 * @param updateCallback, single callback method that returns string on update.
 * @param scrollDirection, vertical / horizontal. Used only in isScrollBarPresent.
 * @param updateOnResize, boolean, if you need it to update on screen resize.
 * @constructor
 */
export function ManageScroll(scrollCont, updateCallback, scrollDirection, updateOnResize) {
  const onUpdate = updateCallback;
  const scrollContainer = document.querySelector(scrollCont);

  if (!scrollContainer) {
    this.setDisplay = () => {};
    this.removeListeners = () => {};
    return;
  }

  this.setDisplay = () => {
    if (isScrollBarPresent(scrollContainer)) {
      onUpdate(ManageScroll.hasScrollBar);
    } else {
      onUpdate(ManageScroll.noScrollBar);
    }
  };

  const onScroll = ({ target: { scrollTop, clientHeight, scrollHeight } }) => {
    if (scrollTop + clientHeight >= scrollHeight) {
      onUpdate(ManageScroll.scrollBottom);
    } else {
      onUpdate(ManageScroll.scrollUpdate);
    }
  };

  const setListeners = () => {
    scrollContainer.addEventListener("scroll", onScroll);
    if (updateOnResize) window.addEventListener("resize", this.setDisplay);
  };

  this.removeListeners = () => {
    scrollContainer.removeEventListener("scroll", onScroll);
    if (updateOnResize) window.removeEventListener("resize", this.setDisplay);
  };

  setListeners();
}

ManageScroll.scrollUpdate = "scrollUpdate";
ManageScroll.scrollBottom = "scrollBottom";
ManageScroll.hasScrollBar = "hasScrollBar";
ManageScroll.noScrollBar = "noScrollBar";

/**
 * tracks clicks/taps outside given container for hiding it.
 *
 * @param flyoutCont, can be class or id, element to be hidden.
 * @param clickCallback, single callback method that acts upon click event.
 * @constructor
 */
export function ManageClick(flyoutCont, clickCallback) {
  const onClick = clickCallback;
  const flyoutContainer = document.querySelector(flyoutCont);

  if (!flyoutContainer) {
    this.removeListeners = () => {};
    return;
  }

  const setListeners = () => {
    flyoutContainer.addEventListener("click", onClick);
  };

  this.removeListeners = () => {
    flyoutContainer.removeEventListener("click", onClick);
  };

  setListeners();
}

export function removeSpecialCharaters(str) {
  if (!str) return "";
  return str.replace(/[^a-zA-Z]/g, "");
}

/**
 * @template T
 * @param {T[]} array - The array to remove duplicates from
 * @param {function} getUniqueIdentifier - Function that receives an element from the array and returns an unique id for it
 * @return {T[]}
 */
export function removeDuplicates(array, getUniqueIdentifier) {
  return Array.from(new Set(array.map((item) => getUniqueIdentifier(item)))).map((uniqueIdentifier) => {
    return array.find((item) => getUniqueIdentifier(item) === uniqueIdentifier);
  });
}
/**
 * Helper to remove blank properties from an object, it will filter all falsy values
 * @param {*} data Object to remove blank properties
 * @returns
 */
export function removeBlankPropertiesFromObject(data) {
  return Object.entries(data).reduce((a, [k, v]) => (v ? ((a[k] = v), a) : a), {});
}

/**
 * Appends script tag to body with an ID
 *
 * @param sourceUrl: string
 * @param scriptTagId: string
 * @returns {Promise<void>}
 */
export function loadExternalCdnScript(sourceUrl, scriptTagId) {
  if (!document.getElementById(scriptTagId)) {
    const scriptTag = document.createElement("script");
    scriptTag.onerror = function (e) {
      errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_LOAD_EXTCDN, "initPaymentsError", "loadExternalCdnScript", {
        description: JSON.stringify(e)
      });
    };
    scriptTag.src = sourceUrl;
    scriptTag.id = scriptTagId;
    document.body.appendChild(scriptTag);
  }
  return Promise.resolve();
}

/**
 * Generates an array of nulls based on random length between min(inclusive) and max(inclusive). This is just for creating mock data.
 * https://stackoverflow.com/questions/1527803/generating-random-whole-numbers-in-javascript-in-a-specific-range
 * @param min: int
 * @param max: int
 * @returns {any[]}
 */
export function getArray(min, max) {
  const minLength = Math.ceil(min);
  const maxLength = Math.floor(max);
  const len = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;
  return new Array(len).fill(null);
}

export function getLocalizedValue(value) {
  // This is for future localization support
  if (!value) return null;
  return value[LANGUAGES.enus] || value["en_us"];
}

/**
 * Returns the current device orientation for a mobile device.
 * Always landscape for desktop devices.
 * @return {string} ORIENTATION.landscape | ORIENTATION.portrait.
 */
export function getDeviceOrientation() {
  if (window.screen?.orientation) {
    return window.screen.orientation.type.includes(ORIENTATION.landscape) ? ORIENTATION.landscape : ORIENTATION.portrait;
  }

  // iOS/safari still doesn't implement the "Screen Orientation API"
  return Math.abs(+window.orientation) === 90 ? ORIENTATION.landscape : ORIENTATION.portrait;
}

/**
 * Adds the classes for keyboard control when the first tab is pressed
 *
 * @export
 */
export function addAccessibilityOnTab() {
  function handleFirstTab(e) {
    if (e.keyCode === 9) {
      document.body.classList.add("user-is-tabbing");
      window.removeEventListener("keydown", handleFirstTab);
    }
  }

  window.addEventListener("keydown", handleFirstTab);
}

/**
 * Converts xml as string to xml document
 * @param {String} xml
 * @returns {Document}
 */
export function getXMLDoc(xml) {
  return new DOMParser().parseFromString(xml, "text/xml");
}

/**
 * grabs and returns node value from xml doc
 * @param {Document} xml
 * @param {String} node
 * @param {Number} index
 * @returns {String}
 */
export function getXMLNodeValue(xml, node, index = 0) {
  return xml.getElementsByTagName(node)[index]?.childNodes[0]?.nodeValue || "";
}

/**
 * Given an array of values and a target value,
 * this method finds the closest value in the array to the target and returns its index.
 * (Note: if two values are equidistant to the target value, this method returns the second value in order).
 *
 * @export
 * @param {number[]} values
 * @param {number} targetValue
 * @return {number} index of the closest value in the array to the target.
 */
export function findClosestIndex(values, targetValue) {
  if (!values?.length) return -1;
  return values.reduce((bestCandidateIndex, currentCandidate, currentIndex) => {
    const bestCandidate = values[bestCandidateIndex];
    return Math.abs(currentCandidate - targetValue) <= Math.abs(bestCandidate - targetValue) ? currentIndex : bestCandidateIndex;
  }, 0);
}
